import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '../services/api';
import { FeedbackButton } from './FeedbackButton';
import { UnpaidBanner } from './UnpaidBanner';

export function Layout({ children }: { children: React.ReactNode }) {
  const { loginWithRedirect, logout, isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    async function checkAdmin() {
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          const currentUser = await api.getCurrentUser(token);
          setIsAdmin(currentUser.role === 'ADMIN');
        } catch (err) {
          console.error('Failed to check admin status:', err);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    }
    checkAdmin();
  }, [isAuthenticated, getAccessTokenSilently]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header/Navigation */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo & Nav */}
            <div className="flex items-center gap-8">
              <Link 
                to="/" 
                className="text-2xl font-bold text-blue-600 hover:text-blue-700 transition-colors"
              >
                ProPoolPicks
              </Link>
              <nav className="hidden md:flex gap-6">
                <Link 
                  to="/pools" 
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  Pools
                </Link>
                {isAuthenticated && (
                  <Link 
                    to="/profile" 
                    className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                  >
                    Profile
                  </Link>
                )}
                {isAdmin && (
                  <Link 
                    to="/admin" 
                    className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                  >
                    Admin
                  </Link>
                )}
              </nav>
            </div>

            {/* Auth Section */}
            <div>
              {isLoading ? (
                <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              ) : isAuthenticated ? (
                <div className="flex items-center gap-4">
                  <span className="hidden sm:block text-sm text-gray-700">
                    {user?.name || user?.email}
                  </span>
                  <button
                    onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => loginWithRedirect()}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Payment v2: site-wide unpaid balance banner */}
      <UnpaidBanner />

      {/* Main Content */}
      <main className="min-h-[calc(100vh-4rem)]">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-sm text-gray-600">
            <p>© 2025 ProPoolPicks. All rights reserved.</p>
            <p className="mt-2">Supporting multiple fantasy pool formats across various sports</p>
          </div>
        </div>
      </footer>

      {/* Floating Feedback Button */}
      <FeedbackButton />
    </div>
  );
}
